import { useMemo } from "react";
import { Box, Flex, HStack, Text, VStack, chakra } from "@chakra-ui/react";
import { useLocation, useNavigate, Link as RouterLink } from "react-router-dom";
import {
  MediaKey,
  ProfilePictureKey,
  type MediaWithUrls,
} from "@greenflagdate/shared";
import { Icon, type IconName } from "../../../components/Icon/Icon";
import { store, useStoreData } from "../../../lib/store/store";
import { ActionType } from "../../../lib/store/storeActions";
import { useMediaBlob } from "../../../lib/mediaHelpers";
import { useMeasuredVariantPath } from "../../../lib/profileMedia";
import { ModalPage } from "../../../components/ModalPage";

type RowTint = "brand" | "friends" | "neutral";

interface SettingsRow {
  to: string;
  icon: IconName;
  title: string;
  desc: string;
  tint?: RowTint;
  badge?: number;
  // Pill rendered next to the title for a mode the row is currently in (e.g.
  // "Friends Only"). Omitted when the row has nothing to call out.
  label?: string;
}

interface SettingsSection {
  eyebrow: string;
  rows: SettingsRow[];
}

const SECTIONS: SettingsSection[] = [
  {
    eyebrow: "Account",
    rows: [
      {
        to: "/settings/profile",
        icon: "user",
        title: "Profile",
        desc: "Photos, bio, prompts, core values",
      },
      {
        to: "/settings/contact",
        icon: "envelope",
        title: "Contact Preferences",
        desc: "Email, phone, and how we reach you",
        tint: "friends",
      },
    ],
  },
  {
    eyebrow: "Preferences",
    rows: [
      {
        to: "/settings/notification",
        icon: "bell",
        title: "Notification Preferences",
        desc: "Events, matches, sparks, and reminders",
      },
      {
        to: "/settings/dating",
        icon: "heart",
        title: "Dating Preferences",
        desc: "Age range, distance, what you're looking for",
      },
    ],
  },
  {
    eyebrow: "Billing",
    rows: [
      {
        to: "/settings/billing",
        icon: "credit-card",
        title: "Billing",
        desc: "Payment method, event history, receipts",
        tint: "neutral",
      },
    ],
  },
];

const ROW_TINT_STYLES: Record<RowTint, { bg: string; color: string }> = {
  brand: { bg: "bg.brandSoft", color: "fg.brand" },
  friends: { bg: "bg.friendsSoft", color: "bg.friends" },
  neutral: { bg: "bg.subtle", color: "fg.default" },
};

const PROFILE_PICTURE_KEY_ORDER: MediaKey[] = [
  ProfilePictureKey.Headshot,
  ProfilePictureKey.Body,
  ProfilePictureKey.Action,
  ProfilePictureKey.Travel,
  ProfilePictureKey.Friends,
  ProfilePictureKey.Pet,
];

const AVATAR_STYLES = {
  w: "56px",
  h: "56px",
  borderRadius: "full",
  flexShrink: 0,
  boxShadow: "image",
} as const;

const AvatarImage = ({ media }: { media: MediaWithUrls }) => {
  const [setMeasureRef, variantPath] = useMeasuredVariantPath(media.urls.cropped);
  const { blob } = useMediaBlob(variantPath ?? "");
  const imageUrl = useMemo(
    () => (blob ? URL.createObjectURL(blob) : undefined),
    [blob]
  );
  return (
    <Flex
      ref={setMeasureRef}
      {...AVATAR_STYLES}
      align="center"
      justify="center"
      bg={imageUrl ? "bg.subtle" : "bg.brandSoft"}
      color="fg.brand"
      backgroundImage={imageUrl ? `url(${imageUrl})` : undefined}
      backgroundSize="cover"
      backgroundPosition="center"
    >
      {!imageUrl && <Icon name="user-solid" boxSize="50%" />}
    </Flex>
  );
};

const AvatarFallback = () => (
  <Flex
    {...AVATAR_STYLES}
    align="center"
    justify="center"
    bg="bg.brandSoft"
    color="fg.brand"
  >
    <Icon name="user-solid" boxSize="50%" />
  </Flex>
);

export const Settings = () => {
  const storeData = useStoreData();
  const navigate = useNavigate();
  const location = useLocation();
  const entryState = location.state as
    | { animate?: boolean; from?: string }
    | null;

  const firstName = storeData.user?.first_name ?? "";
  const friendsOnly = storeData.user?.friends_only ?? false;

  // Dating Preferences advertises Friends Only mode inline, so the user can
  // see they've paused dating without opening the sub-page. No pill otherwise.
  const sections = useMemo(
    () =>
      SECTIONS.map((section) => ({
        ...section,
        rows: section.rows.map((row) =>
          row.to === "/settings/dating" && friendsOnly
            ? { ...row, label: "Friends Only" }
            : row
        ),
      })),
    [friendsOnly]
  );
  const profileMedia = useMemo(() => {
    const media = storeData.user?.media;
    if (!media) return undefined;
    for (const key of PROFILE_PICTURE_KEY_ORDER) {
      const entry = media[key];
      if (entry) return entry;
    }
    return undefined;
  }, [storeData.user?.media]);

  return (
    <ModalPage
      motionKey="settings-modal"
      title="Settings"
      fallback={entryState?.from ?? "/events"}
      animateEntrance={entryState?.animate !== false}
      bodyBg="bg.canvas"
    >
    <Box maxW="40rem" mx="auto" w="100%" pb="2rem" pt="18px">
      <Flex
        mx="4"
        mt="2"
        mb="6"
        p="4"
        align="center"
        gap="14px"
        bg="bg.canvas"
        border="1px solid"
        borderColor="border.subtle"
        borderRadius="sm"
        boxShadow="card"
      >
        {profileMedia ? (
          <AvatarImage media={profileMedia} />
        ) : (
          <AvatarFallback />
        )}
        <Box flex={1} minW="0">
          <Text
            fontSize="17px"
            fontWeight="semibold"
            lineHeight="1.2"
            color="fg.default"
            m="0"
            mb="1"
            truncate
          >
            {firstName || "Your account"}
          </Text>
          {firstName && (
            <HStack gap="1.5" flexWrap="wrap">
              <chakra.span
                display="inline-flex"
                alignItems="center"
                px="2"
                py="0.5"
                bg="bg.brandTint"
                color="fg.onTint"
                borderRadius="pill"
                fontSize="9px"
                fontWeight="bold"
                letterSpacing="wider"
                textTransform="uppercase"
              >
                Green Flag member
              </chakra.span>
            </HStack>
          )}
        </Box>
        <chakra.button
          onClick={() => navigate("/profile")}
          display="inline-flex"
          alignItems="center"
          gap="1"
          bg="transparent"
          color="fg.brand"
          border="1px solid"
          borderColor="fg.brand"
          borderRadius="pill"
          fontWeight="bold"
          fontSize="11px"
          px="3"
          py="1.5"
          cursor="pointer"
          whiteSpace="nowrap"
          _hover={{ filter: "brightness(1.04)" }}
        >
          View Profile
        </chakra.button>
      </Flex>

      {sections.map((section) => (
        <Box key={section.eyebrow} px="4" pb="2">
          <Text
            fontWeight="semibold"
            fontSize="10px"
            letterSpacing="0.14em"
            textTransform="uppercase"
            color="fg.muted"
            mt="18px"
            mx="1"
            mb="10px"
          >
            {section.eyebrow}
          </Text>
          <VStack
            align="stretch"
            gap="0"
            bg="bg.canvas"
            border="1px solid"
            borderColor="border.subtle"
            borderRadius="sm"
            overflow="hidden"
          >
            {section.rows.map((row, i) => {
              const tint = ROW_TINT_STYLES[row.tint ?? "brand"];
              return (
                <chakra.a
                  key={row.to}
                  as={RouterLink}
                  {...{ to: row.to }}
                  display="flex"
                  alignItems="center"
                  gap="14px"
                  p="14px"
                  bg="bg.canvas"
                  color="fg.default"
                  textDecoration="none"
                  cursor="pointer"
                  borderTop={i === 0 ? "0" : "1px solid"}
                  borderTopColor="border.subtle"
                  transition="background 120ms ease"
                  _hover={{ bg: "#FAFBFB" }}
                  _active={{ bg: "#F4F6F5" }}
                >
                  <Flex
                    w="36px"
                    h="36px"
                    borderRadius="full"
                    align="center"
                    justify="center"
                    flexShrink={0}
                    bg={tint.bg}
                    color={tint.color}
                  >
                    <Icon name={row.icon} boxSize="18px" />
                  </Flex>
                  <Box flex={1} minW="0">
                    <HStack gap="1.5" minW="0">
                      <Text
                        fontSize="15px"
                        fontWeight="medium"
                        lineHeight="1.25"
                        color="fg.default"
                        m="0"
                        truncate
                      >
                        {row.title}
                      </Text>
                      {row.label && (
                        <chakra.span
                          display="inline-flex"
                          alignItems="center"
                          flexShrink={0}
                          px="2"
                          py="0.5"
                          bg="bg.friendsSoft"
                          color="fg.friends"
                          borderRadius="pill"
                          fontSize="9px"
                          fontWeight="bold"
                          letterSpacing="wider"
                          textTransform="uppercase"
                          whiteSpace="nowrap"
                        >
                          {row.label}
                        </chakra.span>
                      )}
                    </HStack>
                    <Text
                      fontSize="12px"
                      color="fg.muted"
                      lineHeight="1.35"
                      mt="0.5"
                      truncate
                    >
                      {row.desc}
                    </Text>
                  </Box>
                  {row.badge !== undefined && (
                    <chakra.span
                      bg="bg.danger"
                      color="white"
                      fontSize="10px"
                      fontWeight="bold"
                      lineHeight="1"
                      px="1.5"
                      py="0.5"
                      borderRadius="pill"
                      mr="1"
                    >
                      {row.badge}
                    </chakra.span>
                  )}
                  <Box flexShrink={0} color="#B9BDC3" display="flex" alignItems="center">
                    <Icon name="chevron-right" boxSize="14px" />
                  </Box>
                </chakra.a>
              );
            })}
          </VStack>
        </Box>
      ))}

      <Box px="4" pt="2" pb="1">
        <chakra.button
          onClick={() => {
            store.dispatch({ type: ActionType.LogOut, params: {} });
          }}
          w="100%"
          px="4"
          py="3"
          bg="bg.canvas"
          border="1px solid"
          borderColor="border.subtle"
          borderRadius="sm"
          fontWeight="semibold"
          fontSize="14px"
          color="fg.danger"
          cursor="pointer"
          textAlign="center"
          transition="background 120ms ease"
          _hover={{ bg: "#FEF5F5" }}
        >
          Sign Out
        </chakra.button>
      </Box>
    </Box>
    </ModalPage>
  );
};
